import { Spacing } from '@persona-id/design-tokens';
import { SubscribeIcon } from '@persona-id/design-tokens/icons';
import { MouseEvent, useState } from 'react';
import styled from 'styled-components';

import { SearchListsSelectTable } from 'dashboard/forms/FormReportTemplate/SearchListsSelectTable';
import { ExpandableContent } from 'dashboard/forms/FormReportTemplate/WatchlistListsSelect/ExpandableContent';
import {
  WatchlistSourceListDisplay,
  WatchlistSubscriptionContainer,
  WatchlistSubscriptionDisplay,
  WatchlistSubscriptionName,
} from 'dashboard/forms/FormReportTemplate/WatchlistListsSelect/WatchlistListsOptionDisplay';
import { WatchlistListOption } from 'dashboard/forms/FormReportTemplate/WatchlistListsSelect/WatchlistListsSelect';
import {
  ModalBody,
  ModalContainer,
  ModalDescription,
  ModalHeader,
} from 'dashboard/layouts/ModalLayout';
import { WatchlistDefaultTag } from 'dashboard/models/report/watchlist';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Paragraph } from 'design-system/components/Text';
import { BASE } from 'design-system/variables/spacing';
import useToggle from 'lib/hooks/useToggle';

const getSubscriptionsFromList = (
  list: string,
  subscriptions: string[],
  subscriptionOptions: WatchlistListOption[],
) => {
  return subscriptionOptions.reduce((subs, option) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (subscriptions.includes(option.value) && option.lists.includes(list)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      subs.push(option.name);
    }
    return subs;
  }, []);
};

interface WatchlistListsSelectModalProps {
  subscribedToDefault: boolean;
  subscriptions: string[];
  lists: string[];
  listOptions: WatchlistListOption[];
  subscriptionOptions: WatchlistListOption[];
  handleCancel: (event?: MouseEvent<HTMLButtonElement>) => void;
  handleSubmit: (values: string[]) => unknown;
}

export const WatchlistListsSelectModal = ({
  subscribedToDefault = true,
  subscriptions = [],
  lists = [],
  listOptions,
  subscriptionOptions,
  handleCancel,
  handleSubmit,
}: WatchlistListsSelectModalProps) => {
  const [defaultSelected, setDefaultSelected] = useState(subscribedToDefault);
  const [subscriptionTab, { enable: showSubscriptionTab, disable: hideSubscriptionTab }] =
    useToggle(subscriptions && subscriptions.length > 0);
  const [selectedSubscriptions, setSubscriptions] = useState(subscriptions);
  const [selectedLists, setLists] = useState(lists);
  const listsFromSubscriptions = new Set(
    defaultSelected
      ? [...listOptions.map((list) => list.value)]
      : selectedSubscriptions.reduce((acc, subscriptionSlug) => {
          const subscription = subscriptionOptions.find(
            (option) => option.value === subscriptionSlug,
          );
          if (subscription) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            acc = [...subscription.lists, ...acc];
          }
          return acc;
        }, []),
  );
  const uniqueLists = new Set([...selectedLists, ...Array.from(listsFromSubscriptions)]);

  return (
    <ModalContainer size='xl'>
      <ModalHeader onClose={handleCancel}>Edit source lists</ModalHeader>
      <ModalDescription>
        Edit category subscriptions and individual source lists for this report template.
      </ModalDescription>
      <ModalBody>
        <TabBar>
          <Tab
            type='button'
            id='watchlist-lists-select-modal__subscription-tab'
            selected={subscriptionTab}
            badge={<StyledSticker variant='grey'>{selectedSubscriptions.length}</StyledSticker>}
            onClick={() => showSubscriptionTab()}
          >
            Category Subscriptions
          </Tab>
          <Tab
            type='button'
            id='watchlist-lists-select-modal__source-lists-tab'
            selected={!subscriptionTab}
            badge={<StyledSticker variant='grey'>{uniqueLists.size}</StyledSticker>}
            onClick={() => hideSubscriptionTab()}
          >
            Individual Lists
          </Tab>
        </TabBar>
        <Spacer height={2} />
        {subscriptionTab ? (
          <SearchListsSelectTable
            key='subscriptions'
            options={subscriptionOptions}
            values={selectedSubscriptions}
            valueDisplayName='Category subscription name'
            categoryDisplayName='Type'
            hideFilters={true}
            selectAllDisplay={
              <WatchlistSubscriptionContainer>
                <WatchlistSubscriptionName>
                  <SubscribeIcon />
                  <span>All subscriptions ({`${subscriptionOptions.length}`})</span>
                </WatchlistSubscriptionName>
                <ExpandableContent id='all-lists' title={`${listOptions.length} lists`}>
                  <ul>
                    {listOptions.map((option) => (
                      <li key={option.value}>
                        <Paragraph size='sm' color='secondary'>
                          {option.name}
                        </Paragraph>
                      </li>
                    ))}
                  </ul>
                </ExpandableContent>
              </WatchlistSubscriptionContainer>
            }
            optionDisplay={(option: WatchlistListOption) => (
              <WatchlistSubscriptionDisplay option={option} listOptions={listOptions} />
            )}
            onChange={(values: string[]) => {
              setDefaultSelected(values.length === subscriptionOptions.length);
              setSubscriptions(values);
            }}
          />
        ) : (
          <SearchListsSelectTable
            key='lists'
            options={listOptions}
            values={selectedLists}
            disabledValues={listsFromSubscriptions}
            valueDisplayName='Individual list name'
            categoryDisplayName='Type'
            hideFilters={true}
            optionDisplay={(option: WatchlistListOption) => (
              <WatchlistSourceListDisplay
                option={option}
                subscriptions={getSubscriptionsFromList(
                  option.value,
                  selectedSubscriptions,
                  subscriptionOptions,
                )}
              />
            )}
            onChange={setLists}
          />
        )}
        <Spacer height={3} />
        <StyledFooter>
          <Button
            type='button'
            id='search-lists-select__cancel'
            variant='basic'
            width='auto'
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type='button'
            id='search-lists-select__submit'
            variant='primary'
            width='auto'
            onClick={(event) => {
              event.preventDefault();

              handleSubmit(
                defaultSelected
                  ? [WatchlistDefaultTag.DefaultNoPep]
                  : [...selectedSubscriptions, ...selectedLists],
              );
            }}
          >{`Apply (${uniqueLists.size} lists)`}</Button>
        </StyledFooter>
      </ModalBody>
    </ModalContainer>
  );
};

const StyledFooter = styled.footer`
  display: grid;
  justify-content: end;
  grid-template-columns: auto auto;
  grid-column-gap: ${BASE * 2.5}px;
`;

const StyledSticker = styled(Sticker)`
  margin-left: ${Spacing[2]};
`;
